import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"

import { WebSource } from "../../../../../model/types"
import { useNana } from "../../../../hooks/useNana"

import SourceIdeaCapture, {
  MosaicNotesHelp,
} from "./source_notes/source_idea_capture"

import { HelpBubble, HelpInline } from "../../../../components/help_bubble"
import LiteratureNotes from "./source_notes/literature_notes"
import ExtractedText from "./source_notes/extracted_text"
import { canUseReadingPane } from "../source_content"
import { chooseInitiallyOpenTab, SourceTabOptions } from "./source_notes_tab"

export const SourceNotesState: {
  sourceTabSelected: SourceTabOptions
  allowCaptureOfReadSource: boolean
} = {
  sourceTabSelected: "literature-notes",
  allowCaptureOfReadSource: false,
}

export default function SourceNotes({ source }: { source: WebSource }) {
  const { model } = useNana()
  const navigate = useNavigate()

  const [allowCaptureOfReadSource, setAllowCaptureOfReadSource1] =
    useState<boolean>(SourceNotesState.allowCaptureOfReadSource)
  const setAllowCaptureOfReadSource = (allow: boolean) => {
    setAllowCaptureOfReadSource1(allow)
    SourceNotesState.allowCaptureOfReadSource = allow
  }

  const showLiteratureNotes = (source: WebSource): boolean =>
    !!(
      source &&
      (source.status == "new" ||
        source.status == "queued" ||
        allowCaptureOfReadSource ||
        !blankContent(source.literatureNotes))
    )

  const showMosaicNotes = (source: WebSource): boolean =>
    !!(
      source &&
      (source.status == "new" ||
        source.status == "queued" ||
        allowCaptureOfReadSource ||
        model.getFleetingIdeasFromSource(source.id).length +
          model.getIdeasFromSource(source.id).length >
          0)
    )

  const showExtractedText = (source: WebSource): boolean => !!source?.hasStoredMarkdown

  const [tabSelected, setTabSelected1] = useState<SourceTabOptions>(
    chooseInitiallyOpenTab(
      SourceNotesState.sourceTabSelected,
      !!showLiteratureNotes(source),
      !!showMosaicNotes(source),
      showExtractedText(source)
    )
  )
  const setTabSelected = (tab: SourceTabOptions) => {
    setTabSelected1(tab)
    SourceNotesState.sourceTabSelected = tab
  }

  useEffect(() => {
    setTabSelected(
      chooseInitiallyOpenTab(
        SourceNotesState.sourceTabSelected,
        !!showLiteratureNotes(source),
        !!showMosaicNotes(source),
        showExtractedText(source)
      )
    )
    setAllowCaptureOfReadSource(false)
  }, [source?.id])

  const prevent = (e: Event) => {
    e.preventDefault()
  }

  const noScrollRef = useRef<HTMLDivElement>(null)

  // No scrolling on the source view. This is normally not possible anyway because it's a fixed layout, but in mobile Safari when the keyboard is open everything goes nuts, and scrolling is very confusing.
  useEffect(() => {
    if (!noScrollRef.current) return
    noScrollRef.current.addEventListener("touchmove", prevent)
    return () => noScrollRef.current?.removeEventListener("touchmove", prevent)
  }, [noScrollRef.current])

  return (
    <>
      <div className="hidden-if-keyboard">
        <Tabs
          tabSelected={tabSelected}
          setTabSelected={setTabSelected}
          showLiteratureNotes={showLiteratureNotes(source)}
          showMosaicNotes={showMosaicNotes(source)}
          showExtractedText={showExtractedText(source)}
          setAllowCaptureOfReadSource={setAllowCaptureOfReadSource}
          showReadingPane={offerReadingPane(source)}
          onOpenReadingView={() => navigate(`/app/reading/${source.id}`)}
        />
      </div>
      <div className="grow flex flex-col min-h-0 gap-2 items-stretch">
        {tabSelected == "literature-notes" && showLiteratureNotes(source) && (
          <LiteratureNotes source={source} />
        )}
        {tabSelected == "permanent-notes" && showMosaicNotes(source) && (
          <SourceIdeaCapture
            source={source}
            allowCaptureOfReadSource={allowCaptureOfReadSource}
          />
        )}
        {tabSelected == "extracted-text" && showExtractedText(source) && (
          <ExtractedText source={source} />
        )}
        {source.status == "read" &&
          !showMosaicNotes(source) &&
          !showLiteratureNotes(source) &&
          tabSelected != "extracted-text" && (
            <div className="p-4 md:p-8 text-sm text-gray-700 dark:text-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-700 dark:text-gray-200">
                No notes taken from this source.
              </div>
              <div
                className="cursor-pointer text-sm text-gray-800 dark:text-gray-300 p-2 hover:text-blue-500"
                onClick={() => setAllowCaptureOfReadSource(true)}
              >
                Add notes
              </div>
            </div>
          )}
      </div>
    </>
  )
}

const Tabs = ({
  tabSelected,
  setTabSelected,
  showLiteratureNotes,
  showMosaicNotes,
  showExtractedText,
  showReadingPane,
  setAllowCaptureOfReadSource,
  onOpenReadingView,
}: {
  tabSelected: SourceTabOptions
  setTabSelected: (tab: SourceTabOptions) => void
  showLiteratureNotes: boolean
  showMosaicNotes: boolean
  showExtractedText: boolean
  showReadingPane: boolean
  setAllowCaptureOfReadSource: (allow: boolean) => void
  onOpenReadingView: () => void
}) => {
  const showTabs = showLiteratureNotes || showMosaicNotes || showExtractedText || showReadingPane

  return (
    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 pr-4">
      {showTabs && (
        <>
          <div className="flex justify-start items-center">
            {showLiteratureNotes && (
              <button
                onClick={() => setTabSelected("literature-notes")}
                className={`${
                  tabSelected === "literature-notes"
                    ? "bg-gray-100 dark:bg-gray-800"
                    : "bg-white dark:bg-gray-900"
                } py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`}
              >
                Literature notes
              </button>
            )}
            {showMosaicNotes && (
              <button
                onClick={() => setTabSelected("permanent-notes")}
                className={`${
                  tabSelected === "permanent-notes"
                    ? "bg-gray-100 dark:bg-gray-800"
                    : "bg-white dark:bg-gray-900"
                } py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500`}
              >
                Mosaic notes
              </button>
            )}
            {showExtractedText && (
              <button
                onClick={() => setTabSelected("extracted-text")}
                className={`${
                  tabSelected === "extracted-text"
                    ? "bg-gray-100 dark:bg-gray-800"
                    : "bg-white dark:bg-gray-900"
                } py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500`}
                data-testid="source-extracted-text-tab"
              >
                Extracted text
              </button>
            )}
            {showReadingPane && (
              <button
                onClick={onOpenReadingView}
                className="bg-white dark:bg-gray-900 py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500"
              >
                Reading view
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(!showLiteratureNotes || !showMosaicNotes) && (
              <div
                className="cursor-pointer text-sm text-gray-800 dark:text-gray-300 p-2 hover:text-blue-500"
                onClick={() => setAllowCaptureOfReadSource(true)}
              >
                Add notes
              </div>
            )}
            {tabSelected != "extracted-text" && (
              <HelpBubble
                title={
                  tabSelected == "literature-notes"
                    ? "Taking notes on sources"
                    : "Taking quick notes for the mosaic"
                }
              >
                {tabSelected == "literature-notes" && <NotesHelp />}
                {tabSelected == "permanent-notes" && <MosaicNotesHelp />}
              </HelpBubble>
            )}
          </div>
        </>
      )}
    </div>
  )
}

const NotesHelp = () => {
  return (
    <div className="">
      <p>Take two kinds of notes from each source:</p>
      <ol>
        <li>
          <p>
            <strong>Literature notes</strong>: take these as you read. Write
            down notes on the topic of the source, and the contents, but also
            your own thoughts on the author's ideas.
          </p>
        </li>
        <li>
          <p>
            <strong>Mosaic notes</strong>: from the literature notes extract a
            set of ideas that can be permanently placed into the mosaic.
          </p>
        </li>
      </ol>
      <p>Once you're done, mark the source as Read, which archives it.</p>
    </div>
  )
}

const offerReadingPane = (source: WebSource) => {
  return canUseReadingPane(source)
}

const blankContent = (content: string | null | undefined) =>
  content == null ||
  content == undefined ||
  content == "" ||
  content == "<p></p>"
